import { CircularProgress } from "@material-ui/core";
import DialogContent from "@material-ui/core/DialogContent";
import { PassProps } from "components/_helpers/PassProps";
import { GridForm, GridInput } from "components/GridForm";
import { NumberInputEx } from "components/inputs";
import { DateInput } from "components/inputs";
import { YesNoInput } from "components/inputs";
import { ProductArticleInput } from "components/inputs/ProductArticleInput";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { SelectEnumInput } from "components/inputs/SelectEnumInput";
import TerminalDatesInput from "components/inputs/TerminalDatesInput";
import { PalletType } from "entities/PalletType";
import React from "react";
import { TextInput, maxLength, required, useDataProvider, useTranslate } from "react-admin";
import { useFormState } from "react-final-form";
import { validatePositiveNumber } from "utils/validation/general";

export const AddEditOrderProductArticleFieldBody = ({
	form,
	companyId,
	productArticleReloadKey,
	totalBoxes,
	allOrderProductArticles,
	isAddMode,
	disableEdit = false,
	allowEditOnlyDate = false,
	...rest
}) => {
	const translate = useTranslate();
	const dataProvider = useDataProvider();

	React.useEffect(() => {
		const prefillIsT1FromCompany = async () => {
			if (!isAddMode || !companyId || !form) return;
			try {
				const { data } = await dataProvider.getOne("customer-companies", { id: companyId });
				form.change("is_t1", !!data.cold_storage_is_t1);
			} catch (error) {
				console.error("Failed to load company:", error);
				form.change("is_t1", false);
			}
		};
		prefillIsT1FromCompany();
	}, [isAddMode, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleProductArticleChange = (selectedRecord) => {
		if (selectedRecord && form) {
			form.batch(() => {
				form.change("product_number", selectedRecord.product_number);
				form.change("product_name", selectedRecord.product_name);
				form.change("commodity_name", selectedRecord.commodity_name);
				form.change("box_weight", selectedRecord.default_box_weight);
				form.change("unit_per_pallet", selectedRecord.default_unit_per_pallets);
				form.change("default_temperature", selectedRecord.default_temperature);
				form.change("pallet_type", selectedRecord.default_pallet_type);
			});
		} else {
			form.batch(() => {
				form.change("product_number", null);
				form.change("product_name", null);
				form.change("commodity_name", null);
				form.change("box_weight", null);
				form.change("unit_per_pallet", null);
				form.change("default_temperature", null);
				form.change("pallet_type", null);
			});
		}
	};
	const { values } = useFormState();

	const calculateValue = (values) => {
		return values["unit_per_pallet"] && values["box_weight"]
			? Number((values["unit_per_pallet"] * values["box_weight"]).toFixed(2))
			: 0;
	};

	const validateExpirationDate = (value, allValues) => {
		const productionDate = allValues.production_date;
		if (productionDate && value && new Date(value) < new Date(productionDate)) {
			return translate("osfc.messages.validation.validateExpirationDate");
		}
		return undefined;
	};

	const validateAvailableQuantity = (value, allValues) => {
		if (!totalBoxes) {
			return undefined;
		}
		const totalExistingBoxes = Object.values(allOrderProductArticles).reduce((sum, article) => {
			if (!isAddMode && allValues.id === article.id) {
				return sum;
			}
			return sum + (article.quantity || 0);
		}, 0);
		const totalBoxesSum = totalExistingBoxes + value;
		if (totalBoxesSum > totalBoxes) {
			return translate("osfc.messages.validation.exceedsTotalBoxes", {
				total: totalBoxes,
				available: Math.max(0, totalBoxes - totalExistingBoxes),
			});
		}

		return undefined;
	};

	React.useEffect(() => {
		form.change("weight_per_pallet", calculateValue(values));
	}, [values["box_weight"], values["unit_per_pallet"], values["quantity"]]);

	return (
		<PassProps
			component={DialogContent}
			childPropKeys={["resource", "record"]}
			resource="order-product-articles"
			{...rest}
		>
			<GridForm>
				{/* <GridInput
					md={12}
					component={ProductArticleInput}
					key={productArticleReloadKey}
					source="product_article_id"
					companyId={companyId}
					validate={[required()]}
					onProductArticleChange={handleProductArticleChange}
				/> */}
				<GridInput
					source="product_article_id"
					md={12}
					component={ProductArticleInput}
					companyId={companyId}
					validate={[required()]}
					onChange={handleProductArticleChange}
					record={rest.record}
					label="resources.product-articles.fields.product_name"
					disabled={disableEdit || allowEditOnlyDate}
				/>

				<GridInput
					md={6}
					component={TextInput}
					source="product_number"
					label="resources.product-articles.fields.product_number"
					disabled
				/>
				<GridInput
					md={6}
					component={TextInput}
					source="product_name"
					label="resources.product-articles.fields.product_name"
					disabled
				/>
				<GridInput
					md={6}
					component={NumberInputEx}
					type="float"
					source="box_weight"
					digitsCount={2}
					step={0.5}
					validate={[required(), validatePositiveNumber(translate)]}
					label="resources.order-product-articles.fields.box_weight"
					disabled={disableEdit}
				/>
				<GridInput
					md={6}
					component={TextInput}
					source="commodity_name"
					label="resources.order-product-articles.fields.commodity_name"
					disabled
				/>
				<GridInput md={6}>
					<CoolerTypeSelect
						source="default_temperature"
						label="resources.product-articles.fields.default_temperature"
						disabled
					/>
				</GridInput>
				<GridInput
					md={6}
					component={SelectEnumInput}
					source="pallet_type"
					validate={[required()]}
					label="resources.order-product-articles.fields.pallet_type"
					enumName="pallet_type"
					enumObj={PalletType}
					disabled={disableEdit || allowEditOnlyDate}
				/>
				<GridInput
					md={6}
					component={TextInput}
					source="batch_number"
					validate={[maxLength(64)]}
					label="resources.order-product-articles.fields.batch_number"
					disabled={disableEdit || allowEditOnlyDate}
				/>
				<GridInput
					md={6}
					component={TextInput}
					source="brand"
					label="resources.order-product-articles.fields.brand"
					validate={[maxLength(64)]}
					disabled={disableEdit || allowEditOnlyDate}
				/>

				<GridInput
					md={2}
					component={NumberInputEx}
					source="quantity"
					label="resources.order-product-articles.fields.quantity"
					validate={[required(), validatePositiveNumber(translate), validateAvailableQuantity]}
					disabled={disableEdit || allowEditOnlyDate}
				/>

				<GridInput
					md={2}
					component={NumberInputEx}
					source="unit_per_pallet"
					label="resources.order-product-articles.fields.unit_per_pallet"
					step={1}
					validate={[required(), validatePositiveNumber(translate)]}
					disabled={disableEdit || allowEditOnlyDate}
				/>
				<GridInput
					md={2}
					component={NumberInputEx}
					type="float"
					source="weight_per_pallet"
					digitsCount={2}
					step={0.5}
					label="resources.order-product-articles.fields.weight_per_pallet"
					disabled
				/>
				<GridInput
					md={3}
					component={YesNoInput}
					source="is_need_place_pallets_nearby"
					label="resources.order-product-articles.fields.is_need_place_pallets_nearby"
					defaultValue={false}
					disabled={disableEdit || allowEditOnlyDate}
				/>
				<GridInput
					md={3}
					component={YesNoInput}
					source="is_t1"
					label="resources.order-product-articles.fields.is_t1"
					defaultValue={false}
					disabled={disableEdit || allowEditOnlyDate}
				/>
				<GridInput xs={12} sm={6}>
					<TerminalDatesInput
						component={DateInput}
						source="production_date"
						label="resources.order-product-articles.fields.production_date"
						disabled={disableEdit}
					/>
				</GridInput>
				<GridInput xs={12} sm={6}>
					<TerminalDatesInput
						component={DateInput}
						source="expiration_date"
						label="resources.order-product-articles.fields.expiration_date"
						validate={[validateExpirationDate]}
						disabled={disableEdit}
					/>
				</GridInput>
			</GridForm>
		</PassProps>
	);
};
